import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData } from "../../services/request"

const statusFilters = [
  { key: "ALL", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "paid", label: "Paid" }
]

const AdminPayouts = () => {
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")

  const fetchPayouts = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/payouts`
    const params = { limit: 200, skip: 0 }
    
    if (statusFilter && statusFilter !== 'ALL') {
      params.status = statusFilter
    }

    const res = await getData(url, {}, params)
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setPayouts(res.result.data?.data || [])
      } else {
        setError(res.result.data?.message || 'Failed to load payouts')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchPayouts()
  }, [statusFilter])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return payouts
    
    return payouts.filter(p => {
      const hospitalName = p.hospitalId?.organization || p.hospitalId?.name || ''
      const patientName = p.patientId?.name || ''
      const transactionId = p.paymentId?.transactionId || ''
      const amount = String(p.amount || '')
      
      return (
        hospitalName.toLowerCase().includes(q) ||
        patientName.toLowerCase().includes(q) ||
        transactionId.toLowerCase().includes(q) ||
        amount.includes(q)
      )
    })
  }, [payouts, query])

  const formatCurrency = (amount, currency = 'NGN') => {
    const num = Number(amount)
    if (isNaN(num)) return amount
    return `${currency === 'NGN' ? '₦' : currency} ${num.toLocaleString()}`
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700'
      case 'approved':
        return 'bg-blue-100 text-blue-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'rejected':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>
          <p className="text-sm text-gray-600">Review and approve hospital payouts</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search payouts (hospital, patient, amount...)"
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>
      </div>

      {/* Status Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {statusFilters.map((filter) => (
          <button
            key={filter.key}
            onClick={() => setStatusFilter(filter.key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
              statusFilter === filter.key
                ? 'border-black bg-black text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No payouts found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Hospital</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Patient</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((payout) => (
                  <tr key={payout._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        <Link
                          to={`/admin/app/hospitals/${payout.hospitalId?._id || payout.hospitalId}`}
                          className="text-black hover:underline"
                        >
                          {payout.hospitalId?.organization || payout.hospitalId?.name || 'Unknown'}
                        </Link>
                      </div>
                      {payout.hospitalId?.email && (
                        <div className="text-xs text-gray-500">{payout.hospitalId.email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {payout.patientId?.name || 'Unknown'}
                      </div>
                      {payout.patientId?.email && (
                        <div className="text-xs text-gray-500">{payout.patientId.email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(payout.amount, payout.currency)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(payout.status)}`}>
                        {payout.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(payout.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {payout.postId && (
                          <Link
                            to={`/admin/app/requests/${payout.postId?._id || payout.postId}`}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Request
                          </Link>
                        )}
                        <Link
                          to={`/admin/app/payouts/${payout._id}`}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminPayouts

